import { API } from '../Api';
import type { MediaRef, OrdererKind } from './brand-deal.api';

// Quảng cáo trên livestream: vị trí của creator, đơn đặt quảng cáo, overlay OBS công khai.
// Kiểu dữ liệu khớp DTO bên payment-service (JSON camelCase).

const LIVE_BASE = '/v1/payment-service/brand-live';
const BOOKING_BASE = '/v1/payment-service/brand-bookings';
const PUBLIC_BASE = '/v1/payment-service/public/brand-live';

export type BrandLiveShape = 'Corner' | 'BottomBar' | 'Vertical';
export type BrandLivePlan = 'Monthly' | 'Hourly';

// ── Overlay công khai ──
export interface BrandLiveAd {
  dealId: string;
  logoUrl: string;
  title: string;
  line: string | null;
  goUrl: string;
}

export interface BrandLiveOverlay {
  shape: BrandLiveShape;
  rotateSeconds: number;
  ads: BrandLiveAd[];
}

// ── Vị trí ──
export interface BrandLiveSlot {
  id: string;
  name: string;
  shape: BrandLiveShape;
  maxAds: number;
  rotateSeconds: number;
  overlayKey: string;
  occupied: number;
  createdAt: string;
}

export interface SaveBrandLiveSlotRequest {
  name: string;
  shape: BrandLiveShape;
  maxAds: number;
  rotateSeconds: number;
}

// ── Buổi live ──
export interface BrandLiveSession {
  id: string;
  startedAt: string;
  endedAt: string | null;
  liveUrl: string | null;
  linkDueAt: string | null;
  linkMissing: boolean;
  countedSeconds: number;
}

export interface BrandLiveDealSession {
  sessionId: string;
  startedAt: string;
  endedAt: string | null;
  liveUrl: string | null;
  linkMissing: boolean;
  seconds: number;
  voidedSeconds: number;
  reportStatus: string | null;
  reportId: string | null;
  /** Hạn báo gian lận; null khi buổi còn mở. */
  reportDeadline: string | null;
  canReport: boolean;
}

// ── Bảng giá công khai ──
export interface BrandLiveSlotPublic {
  id: string;
  name: string;
  shape: BrandLiveShape;
  maxAds: number;
  rotateSeconds: number;
  freeSeats: number;
}

export interface BrandLiveRateCard {
  creatorId: string;
  postPriceVnd: number | null;
  liveMonthlyPriceVnd: number | null;
  liveMonthlyHours: number | null;
  liveHourlyPriceVnd: number | null;
  minHours: number;
  slots: BrandLiveSlotPublic[];
}

// ── Đơn đặt quảng cáo ──
export interface BookingPostPart {
  caption: string;
  images: MediaRef[];
  videos: MediaRef[];
  holdDays: number;
}

export interface BookingLivePart {
  plan: BrandLivePlan;
  hours?: number | null;
  validDays?: number | null;
  slotId: string;
  cardLogo: MediaRef | null;
  cardTitle: string;
  cardLine?: string | null;
}

export interface CreateBrandBookingRequest {
  adAccountId?: string | null;
  creatorId: string;
  destinationUrl: string;
  post?: BookingPostPart | null;
  live?: BookingLivePart | null;
}

export interface BrandBookingPost {
  dealId: string;
  status: string;
  priceVnd: number;
  caption: string;
  images: MediaRef[];
  videos: MediaRef[];
  holdDays: number;
  postId: string | null;
  endsAt: string | null;
}

export interface BrandBookingLive {
  dealId: string;
  status: string;
  priceVnd: number;
  plan: BrandLivePlan;
  purchasedSeconds: number;
  shownSeconds: number;
  validDays: number;
  startsAt: string | null;
  endsAt: string | null;
  stoppedAt: string | null;
  stopReason: string | null;
  /** Normal | PayAllValid | Forfeit */
  settleMode: string;
  /** Đang có yêu cầu huỷ chờ admin: của creator (cancelReasonCode = BrandScandal) hoặc của doanh nghiệp (null). */
  pendingCancel: boolean;
  cancelReasonCode: string | null;
  paidGrossVnd: number;
  paidFeeVnd: number;
  slotId: string;
  slotName: string;
  slotShape: BrandLiveShape;
  cardLogo: MediaRef;
  cardTitle: string;
  cardLine: string | null;
  shareCode: string;
}

export interface BrandBooking {
  ordererKind?: OrdererKind;
  campaignId: string;
  campaignStatus: string;
  creatorId: string;
  creatorName: string;
  businessName: string;
  destinationUrl: string;
  respondBy: string | null;
  totalVnd: number;
  post: BrandBookingPost | null;
  live: BrandBookingLive | null;
  createdAt: string;
  threadId?: string | null;
}

export interface AcceptBrandBookingResponse {
  booking: BrandBooking;
  notice?: string | null;
  message?: string | null;
}

// ── Công khai (OBS, không đăng nhập) ──
export const getBrandLiveOverlay = (slotId: string, key: string) =>
  API.get<BrandLiveOverlay>(`${PUBLIC_BASE}/slots/${slotId}`, { params: { key } }).then(
    (r) => r.data
  );

// Trả lại danh sách quảng cáo hiện tại sau khi tính phút.
export const sendBrandLiveBeat = (slotId: string, key: string) =>
  API.post<BrandLiveOverlay>(`${PUBLIC_BASE}/slots/${slotId}/beat`, {}, { params: { key } }).then(
    (r) => r.data
  );

export const getBrandLiveRateCard = (creatorId: string) =>
  API.get<BrandLiveRateCard | null>(`${PUBLIC_BASE}/creators/${creatorId}/rate-card`).then(
    (r) => r.data
  );

// ── Creator: vị trí + buổi live ──
export const listBrandLiveSlots = () =>
  API.get<BrandLiveSlot[]>(`${LIVE_BASE}/slots`).then((r) => r.data);

export const createBrandLiveSlot = (body: SaveBrandLiveSlotRequest) =>
  API.post<BrandLiveSlot>(`${LIVE_BASE}/slots`, body).then((r) => r.data);

export const updateBrandLiveSlot = (id: string, body: SaveBrandLiveSlotRequest) =>
  API.put<BrandLiveSlot>(`${LIVE_BASE}/slots/${id}`, body).then((r) => r.data);

export const deleteBrandLiveSlot = (id: string) =>
  API.delete(`${LIVE_BASE}/slots/${id}`).then((r) => r.data);

export const rotateBrandLiveSlotKey = (id: string) =>
  API.post<BrandLiveSlot>(`${LIVE_BASE}/slots/${id}/rotate-key`, {}).then((r) => r.data);

export const listBrandLiveSessions = (take = 50) =>
  API.get<BrandLiveSession[]>(`${LIVE_BASE}/sessions`, { params: { take } }).then((r) => r.data);

export const setBrandLiveSessionLink = (id: string, liveUrl: string) =>
  API.put<BrandLiveSession>(`${LIVE_BASE}/sessions/${id}/link`, { liveUrl }).then((r) => r.data);

export const listBrandLiveDealSessions = (dealId: string) =>
  API.get<BrandLiveDealSession[]>(`${LIVE_BASE}/deals/${dealId}/sessions`).then((r) => r.data);

// ── Đơn đặt quảng cáo ──
export const createBrandBooking = (body: CreateBrandBookingRequest) =>
  API.post<BrandBooking>(BOOKING_BASE, body).then((r) => r.data);

export const listBusinessBrandBookings = () =>
  API.get<BrandBooking[]>(`${BOOKING_BASE}/business`).then((r) => r.data);

export const listCreatorBrandBookings = () =>
  API.get<BrandBooking[]>(`${BOOKING_BASE}/creator`).then((r) => r.data);

export const getBrandBooking = (campaignId: string) =>
  API.get<BrandBooking>(`${BOOKING_BASE}/${campaignId}`).then((r) => r.data);

export const acceptBrandBooking = (campaignId: string) =>
  API.post<AcceptBrandBookingResponse>(`${BOOKING_BASE}/${campaignId}/accept`, {}).then(
    (r) => r.data
  );

export const declineBrandBooking = (campaignId: string) =>
  API.post<BrandBooking>(`${BOOKING_BASE}/${campaignId}/decline`, {}).then((r) => r.data);

// ── Huỷ hợp tác, báo gian lận, giải trình ──
export type BrandLiveCancelReasonCode = 'BrandScandal' | 'Other';

export interface BrandLiveReport {
  ordererKind?: OrdererKind;
  id: string;
  dealId: string;
  sessionId: string;
  sessionStartedAt: string;
  sessionEndedAt: string | null;
  liveUrl: string | null;
  reason: string;
  creatorResponse: string | null;
  responseDueAt: string;
  status: 'Open' | 'Upheld' | 'Dismissed';
  stopDeal: boolean;
  decisionReason: string | null;
  createdAt: string;
  decidedAt: string | null;
  creatorName: string;
  businessName: string;
  sessionSeconds: number;
}

export interface BrandPartner {
  ordererKind?: OrdererKind;
  adAccountId: string;
  businessName: string;
  logoUrl?: string | null;
}

export const cancelBrandLiveDeal = (dealId: string, reasonCode: BrandLiveCancelReasonCode, reasonText: string) =>
  API.post<BrandBooking>(`${LIVE_BASE}/deals/${dealId}/cancel`, { reasonCode, reasonText }).then((r) => r.data);

export const reportBrandLiveSession = (dealId: string, sessionId: string, reason: string) =>
  API.post<BrandLiveReport>(`${LIVE_BASE}/deals/${dealId}/sessions/${sessionId}/report`, { reason }).then(
    (r) => r.data
  );

export const respondBrandLiveReport = (id: string, response: string) =>
  API.post<BrandLiveReport>(`${LIVE_BASE}/reports/${id}/respond`, { response }).then((r) => r.data);

export const listBrandLiveReports = (role: 'creator' | 'business') =>
  API.get<BrandLiveReport[]>(`${LIVE_BASE}/reports`, { params: { role } }).then((r) => r.data);

// Doanh nghiệp creator đang hợp tác (công khai).
export const getBrandPartners = (userId: string) =>
  API.get<BrandPartner[]>('/v1/payment-service/public/brand-partners', { params: { userId } }).then((r) => r.data);

// Doanh nghiệp xin huỷ phần livestream: dùng lại API yêu cầu huỷ có sẵn, nhận mã deal livestream.
export { requestTaskCancel as requestBrandLiveCancel } from './brand-deal.api';

// ── Admin: báo gian lận và yêu cầu dừng livestream (quyết cần mã Authenticator) ──
const ADMIN_LIVE_BASE = '/v1/payment-service/admin/brand-deals';

export interface BrandLiveMinuteLog {
  minuteStart: string;
  seconds: number;
  voidedAt: string | null;
  voidReason: string | null;
}

export interface AdminBrandLiveReport extends BrandLiveReport {
  ownerIdentityName?: string | null;
  /** Chỉ có ở màn chi tiết. */
  minutes?: BrandLiveMinuteLog[] | null;
}

export interface AdminLiveCancelRequest {
  ordererKind?: OrdererKind; ownerIdentityName?: string | null;
  id: string;
  dealId: string;
  campaignId: string;
  creatorName: string;
  businessName: string;
  reasonCode: string;
  reasonText: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  decisionReason: string | null;
  createdAt: string;
  decidedAt: string | null;
  priceVnd: number;
  purchasedSeconds: number;
  shownSeconds: number;
  paidGrossVnd: number;
}

export const adminListLiveReports = (status = 'Open') =>
  API.get<AdminBrandLiveReport[]>(`${ADMIN_LIVE_BASE}/live-reports`, { params: { status } }).then((r) => r.data);

export const adminGetLiveReport = (id: string) =>
  API.get<AdminBrandLiveReport>(`${ADMIN_LIVE_BASE}/live-reports/${id}`).then((r) => r.data);

export const adminDecideLiveReport = (
  id: string,
  totpCode: string,
  body: { upheld: boolean; stopDeal: boolean; reason: string }
) =>
  API.post<AdminBrandLiveReport>(`${ADMIN_LIVE_BASE}/live-reports/${id}/decide`, body, { params: { totpCode } }).then(
    (r) => r.data
  );

export const adminListLiveCancelRequests = (status = 'Pending') =>
  API.get<AdminLiveCancelRequest[]>(`${ADMIN_LIVE_BASE}/live-cancel-requests`, { params: { status } }).then(
    (r) => r.data
  );

export const adminDecideLiveCancelRequest = (id: string, approve: boolean, totpCode: string, reason: string) =>
  API.post<AdminLiveCancelRequest>(
    `${ADMIN_LIVE_BASE}/live-cancel-requests/${id}/${approve ? 'approve' : 'reject'}`,
    { reason },
    { params: { totpCode } }
  ).then((r) => r.data);
